import React, { useState, useEffect } from "react";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Dialog from "@mui/material/Dialog";
import { Slide } from "@mui/material";
import PropTypes from "prop-types";

// Material Dashboard components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Layout components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// Custom hooks
import useMemberData from "layouts/tables/data/singleMemberTableData";
import Swal from "sweetalert2";
import Grid from "@mui/material/Grid";

// Correct Transition component implementation
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="down" ref={ref} {...props} />;
});
Transition.displayName = "Transition";

// Helper component for detail items
const DetailItem = ({ label, value, icon }) => (
  <MDBox
    mb={2}
    p={2}
    sx={{
      backgroundColor: "rgba(0,0,0,0.02)",
      borderRadius: "8px",
      border: "1px solid rgba(0,0,0,0.1)",
    }}
  >
    <MDBox display="flex" alignItems="center" mb={0.5}>
      {icon && <Icon sx={{ mr: 1, color: "info.main" }}>{icon}</Icon>}
      <MDTypography variant="caption" fontWeight="bold" color="info">
        {label}
      </MDTypography>
    </MDBox>
    <MDTypography variant="body2" sx={{ pl: icon ? 4 : 0 }}>
      {value || "Not provided"}
    </MDTypography>
  </MDBox>
);

DetailItem.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.any,
  icon: PropTypes.string,
};

function Member() {
  useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = `
        .swal2-container {
          z-index: 9999 !important;
        }
      `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const { isLoading, error, selectedMember, MemberDetailsModal, showModalOpen, setShowModalOpen } =
    useMemberData();

  if (isLoading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={6} pb={3} display="flex" justifyContent="center">
          <MDTypography>Loading your member profile...</MDTypography>
        </MDBox>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={6} pb={3} display="flex" justifyContent="center">
          <MDTypography color="error">Error: {error}</MDTypography>
        </MDBox>
      </DashboardLayout>
    );
  }

  if (!selectedMember) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={6} pb={3} display="flex" justifyContent="center">
          <MDTypography>No member data found.</MDTypography>
        </MDBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Card>
          <MDBox
            mx={2}
            mt={-3}
            py={3}
            px={2}
            variant="gradient"
            bgColor="info"
            borderRadius="lg"
            coloredShadow="info"
          >
            <Grid container alignItems="center" justifyContent="space-between">
              <Grid item>
                <MDTypography variant="h6" color="white">
                  My Member Profile
                </MDTypography>
              </Grid>
              <Grid item>
                <MDButton variant="gradient" color="dark" onClick={() => setShowModalOpen(true)}>
                  <Icon>visibility</Icon>&nbsp;View Full Details
                </MDButton>
              </Grid>
            </Grid>
          </MDBox>

          <MDBox p={4}>
            {/* Member Header with Photo */}
            <MDBox
              display="flex"
              alignItems="center"
              mb={4}
              p={3}
              sx={{
                backgroundColor: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                borderRadius: "12px",
                background:
                  "linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)",
              }}
            >
              {selectedMember.photo ? (
                <MDBox
                  component="img"
                  src={`https://api.ofi-cia.com/storage/${selectedMember.photo}`}
                  alt="Member"
                  sx={{
                    width: "120px",
                    height: "120px",
                    borderRadius: "50%",
                    objectFit: "cover",
                    mr: 3,
                    border: "4px solid white",
                    boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
                  }}
                />
              ) : (
                <MDBox
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  sx={{
                    width: "120px",
                    height: "120px",
                    borderRadius: "50%",
                    backgroundColor: "info.main",
                    mr: 3,
                    color: "white",
                  }}
                >
                  <Icon sx={{ fontSize: "3rem" }}>person</Icon>
                </MDBox>
              )}
              <MDBox>
                <MDTypography variant="h4" fontWeight="bold" mb={1}>
                  {selectedMember.firstName} {selectedMember.middleName} {selectedMember.surname}
                </MDTypography>
                <MDBox display="flex" alignItems="center" mb={1}>
                  <Icon sx={{ mr: 1, color: "info.main" }}>badge</Icon>
                  <MDTypography variant="h6" color="info">
                    ID: {selectedMember.memberId || "Not assigned"}
                  </MDTypography>
                </MDBox>
                <MDBox display="flex" alignItems="center">
                  <Icon
                    sx={{
                      mr: 1,
                      color: selectedMember.status === "active" ? "success.main" : "error.main",
                    }}
                  >
                    {selectedMember.status === "active" ? "check_circle" : "cancel"}
                  </Icon>
                  <MDTypography
                    variant="button"
                    color={selectedMember.status === "active" ? "success" : "error"}
                    fontWeight="bold"
                  >
                    {selectedMember.status?.toUpperCase()}
                  </MDTypography>
                </MDBox>
              </MDBox>
            </MDBox>

            {/* Basic Information Section */}
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <MDBox
                  mb={3}
                  p={2}
                  sx={{
                    backgroundColor: "rgba(102, 126, 234, 0.05)",
                    borderRadius: "12px",
                    border: "1px solid rgba(102, 126, 234, 0.2)",
                  }}
                >
                  <MDTypography variant="h5" fontWeight="bold" mb={3} color="info">
                    <Icon sx={{ mr: 1, verticalAlign: "middle" }}>person_outline</Icon>
                    Personal Information
                  </MDTypography>
                  <DetailItem label="First Name" value={selectedMember.firstName} icon="person" />
                  <DetailItem label="Middle Name" value={selectedMember.middleName} icon="person" />
                  <DetailItem label="Surname" value={selectedMember.surname} icon="person" />
                  <DetailItem label="Gender" value={selectedMember.gender} icon="wc" />
                  <DetailItem
                    label="Date of Birth"
                    value={selectedMember.dateOfBirth}
                    icon="cake"
                  />
                  <DetailItem
                    label="Marital Status"
                    value={selectedMember.maritalStatus}
                    icon="favorite"
                  />
                  <DetailItem
                    label="Phone Number"
                    value={selectedMember.phoneNumber}
                    icon="phone"
                  />
                  <DetailItem
                    label="WhatsApp Number"
                    value={selectedMember.whatsappNumber}
                    icon="chat"
                  />
                  <DetailItem label="Email" value={selectedMember.email} icon="email" />
                </MDBox>
              </Grid>

              <Grid item xs={12} md={6}>
                <MDBox
                  mb={3}
                  p={2}
                  sx={{
                    backgroundColor: "rgba(76, 175, 80, 0.05)",
                    borderRadius: "12px",
                    border: "1px solid rgba(76, 175, 80, 0.2)",
                  }}
                >
                  <MDTypography variant="h5" fontWeight="bold" mb={3} color="success">
                    <Icon sx={{ mr: 1, verticalAlign: "middle" }}>location_on</Icon>
                    Address Information
                  </MDTypography>
                  <DetailItem
                    label="Residential Address"
                    value={selectedMember.residentialAddress}
                    icon="home"
                  />
                  <DetailItem
                    label="Postal Address"
                    value={selectedMember.postalAddress}
                    icon="mail"
                  />
                  <DetailItem label="Region" value={selectedMember.region} icon="public" />
                  <DetailItem
                    label="Home Town"
                    value={selectedMember.homeTown}
                    icon="location_city"
                  />
                  <DetailItem label="Nationality" value={selectedMember.nationality} icon="flag" />
                  <DetailItem
                    label="Place of Birth"
                    value={selectedMember.placeOfBirth}
                    icon="place"
                  />
                </MDBox>
              </Grid>
            </Grid>
          </MDBox>
        </Card>
      </MDBox>

      {/* Full Details Modal */}
      <MemberDetailsModal
        member={selectedMember}
        open={showModalOpen}
        onClose={() => setShowModalOpen(false)}
      />

      <Footer />
    </DashboardLayout>
  );
}

Member.propTypes = {
  isLoading: PropTypes.bool,
  error: PropTypes.string,
  selectedMember: PropTypes.object,
  showModalOpen: PropTypes.bool,
  setShowModalOpen: PropTypes.func,
};

export default Member;
